/**
 * Smart Search — Cross-entity search across Atoms, Topics, Discussions
 *
 * Provides a unified search experience:
 * - Full-text matching across concern, title, summary, discussion content
 * - Entity-type filtering (atom/topic/discussion/user)
 * - Relevance scoring with field-weight boosting
 * - Recent items boost for freshness
 * - Search suggestions from top concerns
 */

import type { AtomRow, TopicRow, DiscussionMessageRow } from '../types/api-rows';

// ── Types ──

export type SearchEntityType = 'atom' | 'topic' | 'discussion';

export interface SearchResult {
  id: string;
  type: SearchEntityType;
  title: string;
  snippet: string;
  concern: string;
  score: number;
  matchedFields: string[];
  createdAt: string;
  /** For atoms: maturity; for topics: atom_count hint */
  metadata: Record<string, string | number>;
}

export interface SearchQuery {
  text: string;
  entityTypes?: SearchEntityType[];
  /** Max results per type */
  limit?: number;
  /** Boost recent items (within N days) */
  recencyBoostDays?: number;
}

export interface SearchResponse {
  query: string;
  results: SearchResult[];
  totalCount: number;
  suggestions: string[];
  timing_ms: number;
}

export interface SearchConfig {
  /** Weight multiplier for title matches */
  titleWeight: number;
  /** Weight multiplier for concern matches */
  concernWeight: number;
  /** Weight multiplier for summary/content matches */
  contentWeight: number;
  /** Boost multiplier for items within recency window */
  recencyBoost: number;
  /** Default max results */
  defaultLimit: number;
  /** Minimum query length */
  minQueryLength: number;
}

// ── Constants ──

export const DEFAULT_SEARCH_CONFIG: SearchConfig = {
  titleWeight: 3.0,
  concernWeight: 2.5,
  contentWeight: 1.0,
  recencyBoost: 1.3,
  defaultLimit: 20,
  minQueryLength: 2,
};

// ── Core Search ──

/**
 * Execute a smart search across all entity types.
 */
export function search(
  query: SearchQuery,
  atoms: AtomRow[],
  topics: TopicRow[],
  discussions: DiscussionMessageRow[],
  config: Partial<SearchConfig> = {}
): SearchResponse {
  const start = Date.now();
  const cfg = { ...DEFAULT_SEARCH_CONFIG, ...config };
  const text = query.text.trim().toLowerCase();

  if (text.length < cfg.minQueryLength) {
    return {
      query: query.text,
      results: [],
      totalCount: 0,
      suggestions: getTopConcerns(atoms),
      timing_ms: Date.now() - start,
    };
  }

  const limit = query.limit ?? cfg.defaultLimit;
  const types = query.entityTypes ?? ['atom', 'topic', 'discussion'];
  const tokens = tokenize(text);
  const now = Date.now();
  const recencyWindow = (query.recencyBoostDays ?? 7) * 24 * 60 * 60 * 1000;

  const results: SearchResult[] = [];

  // Search atoms
  if (types.includes('atom')) {
    for (const atom of atoms) {
      const result = scoreAtom(atom, tokens, cfg, now, recencyWindow);
      if (result) results.push(result);
    }
  }

  // Search topics
  if (types.includes('topic')) {
    for (const topic of topics) {
      const result = scoreTopic(topic, tokens, cfg, now, recencyWindow);
      if (result) results.push(result);
    }
  }

  // Search discussions
  if (types.includes('discussion')) {
    for (const msg of discussions) {
      const result = scoreDiscussion(msg, tokens, cfg, now, recencyWindow);
      if (result) results.push(result);
    }
  }

  // Sort by score descending, take top N
  results.sort((a, b) => b.score - a.score);
  const trimmed = results.slice(0, limit);

  return {
    query: query.text,
    results: trimmed,
    totalCount: results.length,
    suggestions: results.length === 0 ? getTopConcerns(atoms) : [],
    timing_ms: Date.now() - start,
  };
}

// ── Scoring Functions ──

function scoreAtom(
  atom: AtomRow,
  tokens: string[],
  config: SearchConfig,
  now: number,
  recencyWindow: number
): SearchResult | null {
  const fields: Array<{ value: string; weight: number; name: string }> = [
    { value: atom.title ?? '', weight: config.titleWeight, name: 'title' },
    { value: atom.concern, weight: config.concernWeight, name: 'concern' },
    { value: atom.value_axis, weight: config.concernWeight * 0.8, name: 'value_axis' },
    { value: atom.summary ?? '', weight: config.contentWeight, name: 'summary' },
    { value: atom.topic ?? '', weight: config.contentWeight * 0.5, name: 'topic' },
  ];

  const { score, matchedFields } = computeFieldScore(tokens, fields);
  if (score === 0) return null;

  const recencyMultiplier = isRecent(atom.created_at, now, recencyWindow)
    ? config.recencyBoost
    : 1.0;

  return {
    id: atom.id,
    type: 'atom',
    title: atom.title ?? atom.concern,
    snippet: (atom.summary ?? atom.concern).slice(0, 120),
    concern: atom.concern,
    score: score * recencyMultiplier,
    matchedFields,
    createdAt: atom.created_at,
    metadata: {
      maturity: atom.maturity,
      visibility: atom.visibility,
    },
  };
}

function scoreTopic(
  topic: TopicRow,
  tokens: string[],
  config: SearchConfig,
  now: number,
  recencyWindow: number
): SearchResult | null {
  const fields: Array<{ value: string; weight: number; name: string }> = [
    { value: topic.title, weight: config.titleWeight, name: 'title' },
    { value: topic.description ?? '', weight: config.contentWeight, name: 'description' },
    { value: topic.description ?? '', weight: config.concernWeight, name: 'description' },
    { value: topic.content_type ?? '', weight: config.contentWeight * 0.3, name: 'content_type' },
  ];

  const { score, matchedFields } = computeFieldScore(tokens, fields);
  if (score === 0) return null;

  const recencyMultiplier = isRecent(topic.updated_at, now, recencyWindow)
    ? config.recencyBoost
    : 1.0;

  return {
    id: topic.id,
    type: 'topic',
    title: topic.title,
    snippet: (topic.description ?? topic.title).slice(0, 120),
    concern: topic.description || topic.title,
    score: score * recencyMultiplier,
    matchedFields,
    createdAt: topic.created_at,
    metadata: {
      status: topic.status,
      content_type: topic.content_type,
    },
  };
}

function scoreDiscussion(
  msg: DiscussionMessageRow,
  tokens: string[],
  config: SearchConfig,
  now: number,
  recencyWindow: number
): SearchResult | null {
  const fields: Array<{ value: string; weight: number; name: string }> = [
    { value: msg.content, weight: config.contentWeight, name: 'content' },
  ];

  const { score, matchedFields } = computeFieldScore(tokens, fields);
  if (score === 0) return null;

  const recencyMultiplier = isRecent(msg.created_at, now, recencyWindow)
    ? config.recencyBoost
    : 1.0;

  return {
    id: msg.id,
    type: 'discussion',
    title: msg.content.slice(0, 60) + (msg.content.length > 60 ? '...' : ''),
    snippet: msg.content.slice(0, 120),
    concern: '',
    score: score * recencyMultiplier,
    matchedFields,
    createdAt: msg.created_at,
    metadata: {
      topic_id: msg.topic_id,
      upgrade_candidate: msg.upgrade_candidate ? 1 : 0,
    },
  };
}

// ── Helpers ──

function computeFieldScore(
  tokens: string[],
  fields: Array<{ value: string; weight: number; name: string }>
): { score: number; matchedFields: string[] } {
  let totalScore = 0;
  const matchedFields: string[] = [];

  for (const field of fields) {
    const lower = field.value.toLowerCase();
    if (!lower) continue;

    let fieldScore = 0;
    for (const token of tokens) {
      if (lower.includes(token)) {
        // Exact start-of-word match is more valuable
        const startBonus = lower.startsWith(token) || lower.includes(` ${token}`) ? 1.5 : 1.0;
        fieldScore += field.weight * startBonus;
      }
    }

    if (fieldScore > 0) {
      totalScore += fieldScore;
      matchedFields.push(field.name);
    }
  }

  return { score: totalScore, matchedFields };
}

/**
 * Tokenize a query string into searchable tokens.
 */
export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[\s,;.!?，。！？]+/)
    .filter((t) => t.length >= 1)
    .slice(0, 10); // Limit to 10 tokens
}

function isRecent(dateStr: string, now: number, window: number): boolean {
  try {
    return now - new Date(dateStr).getTime() < window;
  } catch {
    return false;
  }
}

/**
 * Extract top topic keywords from atoms for search suggestions.
 * Groups by topic (short, repeating label) instead of concern (unique long sentence).
 */
export function getTopConcerns(atoms: AtomRow[], limit: number = 5): string[] {
  const counts = new Map<string, number>();
  for (const atom of atoms) {
    // Prefer topic (short, repeating) over concern (long, unique)
    const key = (atom.topic ?? atom.concern).toLowerCase().trim();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([topic]) => topic);
}

/**
 * Build autocomplete suggestions from partial query.
 */
export function buildSuggestions(
  partial: string,
  atoms: AtomRow[],
  topics: TopicRow[],
  limit: number = 5
): string[] {
  const lower = partial.toLowerCase().trim();
  if (lower.length < 1) return [];

  const candidates = new Set<string>();

  // Topic matches (short, repeating labels — most useful for suggestions)
  for (const atom of atoms) {
    if (atom.topic && atom.topic.toLowerCase().includes(lower)) {
      candidates.add(atom.topic);
    }
  }

  // Concern matches (fallback for detailed queries)
  for (const atom of atoms) {
    if (atom.concern.toLowerCase().includes(lower)) {
      candidates.add(atom.concern);
    }
  }

  // Topic title matches
  for (const topic of topics) {
    if (topic.title.toLowerCase().includes(lower)) {
      candidates.add(topic.title);
    }
  }

  return [...candidates].slice(0, limit);
}
